import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { AnimatePresence, motion } from "framer-motion";

export interface HourGraphProps {
  data: number[][];
  todate: Date;
  fromdate: Date;
  loading: boolean;
}

interface ChartDataItem {
  date: string;
  fullDate: string;
  isFirstOfMonth?: boolean;
  check: number;
  checkin: number;
  checkout: number;
  index: number;
  isActualData: boolean;
}

interface DataMapItem {
  date: Date;
  check: number;
  checkin: number;
  checkout: number;
  count: number;
  isActualData: boolean;
  index: number;
}

interface YAxisConfig {
  max: number;
  ticks: number[];
  interval?: number;
}

type DisplayMode = 'hourly' | 'daily' | 'monthly' | 'quarterly' | 'yearly';

const COLORS = {
  'check': '#0175FF',
  'checkin': '#00C45E',
  'checkout': '#FF0058'
} as const;

const DISPLAY_MODE_THRESHOLDS = {
  yearly: 900,
  quarterly: 600,
  monthly: 300
} as const;

const HourGraph = (props: HourGraphProps) => {
  const { t, i18n } = useTranslation();
  const [activeLine, setActiveLine] = useState<number | null>(null);

  const formatDateWithLocale = (date: Date, options: Intl.DateTimeFormatOptions) => {
    const locale = i18n.language === 'en' ? 'en-GB' : (i18n.language || 'nl');
    const dateStr = date.toLocaleDateString(locale, options);
    return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  };

  const calculateTotalDays = () => {
    const startDate = new Date(props.fromdate);
    const endDate = new Date(props.todate);

    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays + 1;
  };

  const getDateDisplayMode = (totalDays: number): DisplayMode => {
    if (totalDays === 1) return 'hourly';
    if (totalDays > DISPLAY_MODE_THRESHOLDS.yearly) return 'yearly';
    if (totalDays > DISPLAY_MODE_THRESHOLDS.quarterly) return 'quarterly';
    if (totalDays > DISPLAY_MODE_THRESHOLDS.monthly) return 'monthly';
    return 'daily';
  };

  const getTimeKey = (date: Date, mode: DisplayMode, hour?: number) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    switch (mode) {
      case 'hourly': return `${year}-${month}-${date.getDate()}-${hour}`;
      case 'yearly': return `${year}`;
      case 'quarterly': return `${year}-Q${Math.floor(month / 3) + 1}`;
      case 'monthly': return `${year}-${month}`;
      default: return `${year}-${month}-${date.getDate()}`;
    }
  };

  const getQuarter = (date: Date) => Math.floor(date.getMonth() / 3) + 1;
  const isNewYear = (date: Date) => date.getMonth() === 0 && date.getDate() === 1;
  const isNewYearEve = (date: Date) => date.getMonth() === 11 && date.getDate() === 31;

  const formatHour = (hour: number) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);

    const timeStr = date.toLocaleTimeString([], { hour: 'numeric' });

    return timeStr.replace(/^0+/, '') || '0';
  };

  const formatXAxisDate = (date: Date, mode: DisplayMode, hour?: number) => {
    const formatOptions = {
      hourly: () => formatHour(hour!),
      yearly: () => date.getFullYear().toString(),
      quarterly: () => `Q${getQuarter(date)} ${date.getFullYear()}`,
      monthly: () => {
        if (isNewYear(date) || isNewYearEve(date)) {
          return formatDateWithLocale(date, { year: 'numeric' });
        }
        return formatDateWithLocale(date, { month: 'short' });
      },
      daily: () => {
        if (isNewYear(date) || isNewYearEve(date)) {
          return formatDateWithLocale(date, { year: 'numeric' });
        }
        return formatDateWithLocale(date, { day: 'numeric', month: 'short' });
      }
    };

    return formatOptions[mode]();
  };

  const formatFullDate = (date: Date, mode: DisplayMode, hour?: number) => {
    const formatOptions = {
      hourly: () => {
        const dateStr = formatDateWithLocale(date, { day: 'numeric', month: 'long', year: 'numeric' });
        const timeStr = formatHour(hour!);
        return t('graph:dateAndHour', { date: dateStr, time: timeStr });
      },
      yearly: () => date.getFullYear().toString(),
      quarterly: () => `Q${getQuarter(date)} ${date.getFullYear()}`,
      monthly: () => formatDateWithLocale(date, { month: 'long', year: 'numeric' }),
      daily: () => formatDateWithLocale(date, { day: 'numeric', month: 'long', year: 'numeric' })
    };

    return formatOptions[mode]();
  };

  const getDataValue = (dataArray: number[], index: number) => {
    return dataArray && index < dataArray.length ? dataArray[index] || 0 : 0;
  };

  const createTimeBasedDataItem = (date: Date, mode: DisplayMode, hour?: number): DataMapItem => {
    const indexCalculators = {
      hourly: () => hour || 0,
      yearly: (d: Date) => d.getFullYear(),
      quarterly: (d: Date) => getQuarter(d) + d.getFullYear() * 4,
      monthly: (d: Date) => d.getMonth() + d.getFullYear() * 12,
      daily: () => 0
    };

    return {
      date: new Date(date),
      check: 0,
      checkin: 0,
      checkout: 0,
      count: 0,
      isActualData: false,
      index: indexCalculators[mode](date)
    };
  };

  const generateHourlyData = (date: Date): ChartDataItem[] => {
    const result: ChartDataItem[] = [];

    for (let hour = 0; hour < 24; hour++) {
      const checkValue = getDataValue(props.data?.[0] || [], hour);
      const checkinValue = getDataValue(props.data?.[1] || [], hour);
      const checkoutValue = getDataValue(props.data?.[2] || [], hour);

      result.push({
        date: formatXAxisDate(date, 'hourly', hour),
        fullDate: formatFullDate(date, 'hourly', hour),
        isFirstOfMonth: hour === 0,
        check: checkValue,
        checkin: checkinValue,
        checkout: checkoutValue,
        index: hour,
        isActualData: true
      });
    }

    return result;
  };

  const generateTimeBasedData = (startDate: Date, endDate: Date, mode: DisplayMode) => {
    if (mode === 'daily' || mode === 'hourly') return {};

    const dataMap: { [key: string]: DataMapItem } = {};
    const currentDate = new Date(startDate);

    const setupStartDate = {
      yearly: () => { currentDate.setDate(1); currentDate.setMonth(0); },
      quarterly: () => {
        currentDate.setDate(1);
        currentDate.setMonth(Math.floor(currentDate.getMonth() / 3) * 3);
      },
      monthly: () => { currentDate.setDate(1); },
      daily: () => { },
      hourly: () => { }
    };

    const incrementDate = {
      yearly: () => currentDate.setFullYear(currentDate.getFullYear() + 1),
      quarterly: () => currentDate.setMonth(currentDate.getMonth() + 3),
      monthly: () => currentDate.setMonth(currentDate.getMonth() + 1),
      daily: () => { },
      hourly: () => { }
    };

    setupStartDate[mode]();

    while (currentDate <= endDate) {
      const key = getTimeKey(currentDate, mode);
      dataMap[key] = createTimeBasedDataItem(currentDate, mode);
      incrementDate[mode]();
    }

    return dataMap;
  };

  const createDailyChartItem = (currentDate: Date, isInActualRange: boolean, dataIndex: number): ChartDataItem => ({
    date: formatXAxisDate(currentDate, 'daily'),
    fullDate: formatDateWithLocale(currentDate, { day: 'numeric', month: 'long', year: 'numeric' }),
    isFirstOfMonth: currentDate.getDate() === 1,
    check: isInActualRange ? getDataValue(props.data[0], dataIndex) : 0,
    checkin: isInActualRange ? getDataValue(props.data[1], dataIndex) : 0,
    checkout: isInActualRange ? getDataValue(props.data[2], dataIndex) : 0,
    index: dataIndex,
    isActualData: isInActualRange
  });

  const generateDailyData = (startDate: Date, totalDays: number): ChartDataItem[] => {
    const displayDays = Math.max(7, totalDays);
    const paddingBefore = totalDays < 7 ? Math.floor((displayDays - totalDays) / 2) : 0;
    const result: ChartDataItem[] = [];

    for (let i = -paddingBefore; i < (displayDays - paddingBefore); i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const isInActualRange = i >= 0 && i < totalDays;

      const item = createDailyChartItem(currentDate, isInActualRange, i);
      item.index = i + paddingBefore;
      result.push(item);
    }

    return result;
  };

  const aggregateDataIntoMap = (dataMap: { [key: string]: DataMapItem }, startDate: Date, totalDays: number, mode: DisplayMode) => {
    for (let i = 0; i < totalDays; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const key = getTimeKey(currentDate, mode);

      if (!dataMap[key]) continue;

      dataMap[key].check += getDataValue(props.data[0], i);
      dataMap[key].checkin += getDataValue(props.data[1], i);
      dataMap[key].checkout += getDataValue(props.data[2], i);
      dataMap[key].count++;
      dataMap[key].isActualData = true;
    }

    return dataMap;
  };

  const transformDataMapToArray = (dataMap: { [key: string]: DataMapItem }, mode: DisplayMode): ChartDataItem[] => {
    return Object.values(dataMap)
      .map((data: DataMapItem) => ({
        date: formatXAxisDate(data.date, mode),
        fullDate: formatFullDate(data.date, mode),
        isFirstOfMonth: true,
        check: data.check,
        checkin: data.checkin,
        checkout: data.checkout,
        isActualData: data.isActualData,
        index: data.index
      }))
      .sort((a, b) => a.index - b.index);
  };

  const generateChartData = (): ChartDataItem[] => {
    const startDate = new Date(props.fromdate);
    const endDate = new Date(props.todate);
    const totalDays = calculateTotalDays();
    const mode = getDateDisplayMode(totalDays);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    if (mode === 'hourly') {
      return generateHourlyData(startDate);
    }

    if (mode === 'daily') {
      return generateDailyData(startDate, totalDays);
    }

    const dataMap = generateTimeBasedData(startDate, endDate, mode);
    const aggregatedDataMap = aggregateDataIntoMap(dataMap, startDate, totalDays, mode);
    return transformDataMapToArray(aggregatedDataMap, mode);
  };

  const findMaxValue = (chartData: ChartDataItem[]): number => {
    return chartData.reduce((max, item) =>
      Math.max(max, item.check || 0, item.checkin || 0, item.checkout || 0), 0
    );
  };

  const handleZeroMaxValue = (): YAxisConfig => {
    return { max: 100, ticks: [0, 25, 50, 75, 100] };
  };

  const handleSmallMaxValue = (maxValue: number): YAxisConfig => {
    const niceMax = Math.ceil(maxValue * 1.2);
    const ticks = [];
    for (let i = 0; i <= niceMax; i++) {
      ticks.push(i);
    }
    return {
      max: niceMax,
      ticks: ticks,
      interval: 1
    };
  };

  const calculateTickInterval = (maxValue: number, normalized: number, magnitude: number): number => {
    if (maxValue <= 50) {
      if (maxValue <= 20) return 5;
      else if (maxValue <= 50) return 10;
      else return 25;
    } else if (maxValue <= 200) {
      return maxValue <= 100 ? 25 : 50;
    } else if (maxValue <= 500) {
      return maxValue <= 300 ? 50 : 100;
    } else {
      if (normalized <= 1) return magnitude * 0.25;
      else if (normalized <= 2) return magnitude * 0.5;
      else if (normalized <= 5) return magnitude;
      else return magnitude * 2;
    }
  };

  const calculateNiceMax = (paddedMax: number, tickInterval: number, maxValue: number): number => {
    let niceMax = Math.ceil(paddedMax / tickInterval) * tickInterval;

    if (niceMax > maxValue * 1.4) {
      niceMax = Math.ceil(maxValue * 1.2 / tickInterval) * tickInterval;
    }

    return niceMax;
  };

  const generateBasicTicks = (niceMax: number, tickInterval: number): number[] => {
    const ticks = [];
    for (let i = 0; i <= niceMax; i += tickInterval) {
      const tickValue = Math.round(i);
      ticks.push(tickValue);
    }
    return ticks;
  };

  const enhanceTicksIfNeeded = (ticks: number[], niceMax: number, tickInterval: number): YAxisConfig => {
    if (ticks.length <= 4) {
      const halfInterval = tickInterval / 2;
      const enhancedTicks = [];

      for (let i = 0; i <= niceMax; i += halfInterval) {
        const tickValue = Math.round(i);
        enhancedTicks.push(tickValue);
      }

      const uniqueTicks = [...new Set(enhancedTicks)].sort((a, b) => a - b);

      return {
        max: Math.round(niceMax),
        ticks: uniqueTicks,
        interval: halfInterval
      };
    }

    return {
      max: Math.round(niceMax),
      ticks: ticks,
      interval: tickInterval
    };
  };

  const reduceTicksIfTooMany = (ticks: number[], paddedMax: number, tickInterval: number): YAxisConfig => {
    if (ticks.length > 8) {
      const newTickInterval = tickInterval * 2;
      const newNiceMax = Math.ceil(paddedMax / newTickInterval) * newTickInterval;
      const newTicks = [];

      for (let i = 0; i <= newNiceMax; i += newTickInterval) {
        newTicks.push(Math.round(i));
      }

      return {
        max: Math.round(newNiceMax),
        ticks: newTicks,
        interval: newTickInterval
      };
    }

    return {
      max: Math.round(ticks[ticks.length - 1]),
      ticks: ticks,
      interval: tickInterval
    };
  };

  const calculateYAxis = (chartData: ChartDataItem[]): YAxisConfig => {
    const maxValue = findMaxValue(chartData);

    if (maxValue === 0) {
      return handleZeroMaxValue();
    }

    if (maxValue < 10) {
      return handleSmallMaxValue(maxValue);
    }

    const paddedMax = maxValue * (maxValue < 100 ? 1.15 : 1.1);
    const magnitude = Math.pow(10, Math.floor(Math.log10(paddedMax)));
    const normalized = paddedMax / magnitude;

    const tickInterval = calculateTickInterval(maxValue, normalized, magnitude);
    const niceMax = calculateNiceMax(paddedMax, tickInterval, maxValue);
    const basicTicks = generateBasicTicks(niceMax, tickInterval);

    const enhancedConfig = enhanceTicksIfNeeded(basicTicks, niceMax, tickInterval);
    const finalConfig = reduceTicksIfTooMany(enhancedConfig.ticks, paddedMax, enhancedConfig.interval || tickInterval);

    return finalConfig;
  };

  const formatHeaderDate = (date: Date) => {
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();
    const options = dateYear !== currentYear
      ? { day: 'numeric', month: 'long', year: 'numeric' } as const
      : { day: 'numeric', month: 'long' } as const;
    return formatDateWithLocale(date, options);
  };

  const getHeaderTitle = () => {
    const fromFormatted = formatHeaderDate(props.fromdate);
    const toFormatted = formatHeaderDate(props.todate);

    return fromFormatted === toFormatted
      ? t('overview:NumberOfScansInOneDay', { date: fromFormatted })
      : t('overview:NumberOfScans', { fromdate: fromFormatted, todate: toFormatted });
  };

  const handleLegendItemClick = (index: number) => {
    setActiveLine(activeLine !== index ? index : null);
  };

  const handleLineClick = (index: number) => {
    setActiveLine(activeLine === index ? null : index);
  };

  const createCustomLegend = ({ payload }: any) => (
    <div className="flex mb-2 gap-2">
      {payload.map((entry: any, index: number) => (
        <div
          key={`item-${index}`}
          className={`hover:bg-gray-100 transition-all duration-200 rounded-[0.4rem] cursor-pointer flex items-center gap-2 px-2 py-1 ${activeLine === index && 'bg-gray-200'}`}
          onClick={() => handleLegendItemClick(index)}
        >
          <div
            style={{ backgroundColor: entry.color }}
            className="max-w-[10px] max-h-[10px] min-w-[10px] min-h-[10px] rounded-full"
          />
          <span className="text-sm text-[#5B6C79] max-w-[50px] sm:max-w-[1000px] overflow-hidden text-ellipsis">
            {t(`graph:${entry.value}`)}
          </span>
        </div>
      ))}
    </div>
  );

  const getLineStyle = (lineIndex: number) => ({
    strokeWidth: activeLine === lineIndex ? 4 : (activeLine !== null && activeLine !== lineIndex ? 1 : 2),
    opacity: activeLine !== null && activeLine !== lineIndex ? 0.25 : 1
  });

  const formatTooltip = (value: any, name: any) => [value, t(`graph:${name}`)];

  const formatTooltipLabel = (label: any, payload: any) => {
    return payload?.[0]?.payload?.fullDate || label;
  };

  const formatYAxisTick = (value: number) => (value === 0 ? '' : value.toString());

  const chartData = useMemo(generateChartData, [props.data, props.fromdate, props.todate, i18n.language]);
  const yAxisConfig = useMemo(() => calculateYAxis(chartData), [chartData]);

  // Hide 0 tick line on y-axis
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .recharts-cartesian-axis-tick:first-child .recharts-cartesian-axis-tick-line {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div
      className="relative overflow-hidden"
      style={{
        border: '1px solid #D7E3F2',
        background: 'white',
        minHeight: '450px',
        maxHeight: '450px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '0.4rem'
      }}
    >
      <div className="border-b-[1px] border-[#D7E3F2] flex justify-between items-center bg-black/[1%] cursor-default">
        <h1 className="font-bold px-5 p-4">
          <span className="line-clamp-1 hover:line-clamp-none">
            {getHeaderTitle()}
          </span>
        </h1>
        <FontAwesomeIcon className="mr-5 text-xl opacity-[25%]" icon={faChartLine} />
      </div>

      <div className="flex relative group">
        <AnimatePresence>
          {props.loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute bg-white/50 backdrop-blur-sm w-full h-[400px] z-[100] flex items-center justify-center"
            >
              <FontAwesomeIcon className="mr-5 text-2xl animate-spin text-gray-600" icon={faSpinner} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex w-full h-full">
          <ResponsiveContainer width="97%" height={400}>
            <LineChart data={chartData}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#5B6C79' }}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, yAxisConfig.max]}
                ticks={yAxisConfig.ticks}
                axisLine={true}
                tick={{ fontSize: 14, fill: '#5B6C79' }}
                tickFormatter={formatYAxisTick}
              />
              <Tooltip
                formatter={formatTooltip}
                labelFormatter={formatTooltipLabel}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #D7E3F2',
                  borderRadius: '0.4rem',
                  padding: '8px'
                }}
              />
              <Legend
                content={createCustomLegend}
                verticalAlign="top"
              />
              {['check', 'checkout', 'checkin'].map((dataKey, idx) => (
                <Line
                  key={dataKey}
                  type="monotone"
                  dataKey={dataKey}
                  name={dataKey}
                  stroke={COLORS[dataKey as keyof typeof COLORS]}
                  strokeWidth={getLineStyle(idx).strokeWidth}
                  opacity={getLineStyle(idx).opacity}
                  dot={(dataPoint: { isActualData: any; }) => dataPoint.isActualData}
                  cursor="pointer"
                  onClick={() => handleLineClick(idx)}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default HourGraph;
